import axios from 'axios';
import logger from '../utils/logger';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

class CustomerService {
  /**
   * Create a new customer in HousecallPro via the backend API
   * @param {Object} customerData - Customer information
   * @param {string} customerData.first_name - Customer's first name
   * @param {string} customerData.last_name - Customer's last name
   * @param {string} [customerData.email] - Customer's email address
   * @param {string} [customerData.mobile_number] - Customer's mobile phone number
   * @param {string} [customerData.home_number] - Customer's home phone number
   * @param {string} [customerData.work_number] - Customer's work phone number
   * @param {string} [customerData.company] - Customer's company name
   * @param {string} [customerData.notes] - Additional notes about the customer
   * @returns {Promise<Object>} Created customer data from HousecallPro
   */
  async createCustomer(customerData) {
    try {
      logger.log('[CustomerService] Creating customer:', {
        first_name: customerData.first_name,
        last_name: customerData.last_name,
        email: customerData.email || 'N/A',
        has_mobile: !!customerData.mobile_number
      });

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/customers`,
        customerData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 15000 // 15 second timeout for HCP API calls
        }
      );

      logger.log('[CustomerService] Customer created successfully:', {
        success: response.data.success,
        customerId: response.data.customer?.id
      });

      return response.data;

    } catch (error) {
      logger.error('[CustomerService] Failed to create customer:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });

      // Re-throw with user-friendly message
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (error.response?.status === 400) {
        const details = error.response.data?.details || error.response.data?.error || 'Invalid customer data';
        throw new Error(`Validation error: ${details}`);
      } else if (error.response?.status === 500) {
        const details = error.response.data?.details || 'Server error';
        throw new Error(`Server error: ${details}`);
      } else if (error.code === 'ECONNABORTED') {
        throw new Error('Request timed out. Please try again.');
      } else {
        throw new Error(error.response?.data?.error || error.message || 'Failed to create customer');
      }
    }
  }

  /**
   * Get a customer from HousecallPro by ID
   * @param {string} customerId - The HousecallPro customer ID
   * @returns {Promise<Object>} Customer data from HousecallPro
   */
  async getCustomer(customerId) {
    try {
      logger.log('[CustomerService] Getting customer:', { customerId });

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/customers/${customerId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000 // 10 second timeout
        }
      );

      logger.log('[CustomerService] Customer retrieved successfully:', {
        success: response.data.success,
        customerId: response.data.customer?.id
      });

      return response.data;

    } catch (error) {
      logger.error('[CustomerService] Failed to get customer:', {
        customerId,
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });

      // Re-throw with user-friendly message
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (error.response?.status === 404) {
        throw new Error('Customer not found');
      } else if (error.response?.status === 500) {
        const details = error.response.data?.details || 'Server error';
        throw new Error(`Server error: ${details}`);
      } else if (error.code === 'ECONNABORTED') {
        throw new Error('Request timed out. Please try again.');
      } else {
        throw new Error(error.response?.data?.error || error.message || 'Failed to get customer');
      }
    }
  }

  /**
   * Create a fake customer from maid profile data for testing purposes
   * @param {Object} maidProfile - Maid profile data
   * @returns {Promise<Object>} Created customer data from HousecallPro
   */
  async createFakeCustomerFromMaid(maidProfile) {
    try {
      logger.log('[CustomerService] Creating fake customer from maid profile:', {
        maidId: maidProfile.id,
        firstName: maidProfile.firstName || maidProfile.user?.firstName,
        lastName: maidProfile.lastName || maidProfile.user?.lastName
      });

      // Extract data from maid profile
      const customerData = {
        first_name: maidProfile.firstName || maidProfile.user?.firstName || 'Unknown',
        last_name: maidProfile.lastName || maidProfile.user?.lastName || 'Maid',
        email: maidProfile.email || maidProfile.user?.email || null,
        mobile_number: maidProfile.phoneNumber || maidProfile.user?.phoneNumber || null,
        notes: this.buildNotesFromMaidProfile(maidProfile)
      };

      // Add "Test Customer" prefix to indicate this is fake data
      customerData.first_name = `[MAID] ${customerData.first_name}`;

      return await this.createCustomer(customerData);

    } catch (error) {
      logger.error('[CustomerService] Failed to create fake customer from maid:', {
        maidId: maidProfile?.id,
        error: error.message
      });
      throw error;
    }
  }

  /**
   * Build notes field from maid profile information
   * @param {Object} maidProfile - Maid profile data
   * @returns {string} Notes combining experience and other details
   */
  buildNotesFromMaidProfile(maidProfile) {
    const notes = [];

    // Add test customer indicator
    notes.push('*** TEST CUSTOMER CREATED FROM MAID PROFILE ***');
    notes.push(`Maid ID: ${maidProfile.id}`);

    // Add experience description if available
    if (maidProfile.experienceDescription) {
      notes.push(`\nExperience: ${maidProfile.experienceDescription}`);
    }

    // Add experience level and years
    if (maidProfile.experienceLevel) {
      notes.push(`\nExperience Level: ${maidProfile.experienceLevel}`);
    }
    if (maidProfile.experience) {
      notes.push(`Years of Experience: ${maidProfile.experience}`);
    }

    // Add hourly rate if available
    if (maidProfile.hourlyRate) {
      notes.push(`Hourly Rate: $${maidProfile.hourlyRate}`);
    }

    // Add services offered
    if (maidProfile.servicesOffered && maidProfile.servicesOffered.length > 0) {
      notes.push(`\nServices: ${maidProfile.servicesOffered.join(', ')}`);
    } else if (maidProfile.services && maidProfile.services.length > 0) {
      notes.push(`\nServices: ${maidProfile.services.join(', ')}`);
    }

    // Add languages if available
    if (maidProfile.languages && maidProfile.languages.length > 0) {
      notes.push(`Languages: ${maidProfile.languages.join(', ')}`);
    }

    // Add working preference
    if (maidProfile.workingPreference) {
      notes.push(`Working Preference: ${maidProfile.workingPreference}`);
    }

    // Add creation timestamp
    notes.push(`\nCreated: ${new Date().toISOString()}`);

    // Join all notes and truncate to 254 characters max
    const fullNotes = notes.join('\n');
    
    // Truncate to 254 characters if needed
    if (fullNotes.length > 254) {
      return fullNotes.substring(0, 251) + '...'; // 251 + 3 = 254 chars
    }
    
    return fullNotes;
  }

  /**
   * Create a customer from questionnaire lead data
   * @param {Object} leadData - Lead data from questionnaire_progress table
   * @returns {Promise<Object>} Created customer data from HousecallPro
   */
  async createCustomerFromLead(leadData) {
    try {
      logger.log('[CustomerService] Creating customer from lead:', {
        leadId: leadData.id,
        sessionId: leadData.sessionId?.substring(0, 8),
        stepName: leadData.stepName,
        hasUser: !!leadData.user,
        hasFormData: !!leadData.formData
      });

      // Extract customer data from lead
      const customerData = this.mapLeadToCustomerData(leadData);

      // Validate the mapped data
      const validation = this.validateCustomerData(customerData);
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
      }

      // Create the customer
      return await this.createCustomer(customerData);

    } catch (error) {
      logger.error('[CustomerService] Failed to create customer from lead:', {
        leadId: leadData?.id,
        error: error.message
      });
      throw error;
    }
  }

  /**
   * Map lead data to HCP customer data format
   * @param {Object} leadData - Lead data from questionnaire
   * @returns {Object} Customer data formatted for HCP API
   */
  mapLeadToCustomerData(leadData) {
    const { user, formData } = leadData;
    
    // Extract name - prioritize user data, fallback to formData
    let firstName = '';
    let lastName = '';
    
    if (user?.firstName) {
      firstName = user.firstName;
      lastName = user.lastName || '';
    } else if (formData?.firstName) {
      firstName = formData.firstName;
      lastName = formData.lastName || '';
    } else if (formData?.name) {
      const nameParts = formData.name.trim().split(' ');
      firstName = nameParts[0] || '';
      lastName = nameParts.slice(1).join(' ') || '';
    }

    // Extract contact information - prioritize user data, fallback to formData
    const email = user?.email || formData?.email || null;
    const rawPhone = user?.phoneNumber || formData?.phone || formData?.phoneNumber || null;

    // Validate and sanitize phone number - use "0000000000" as fallback (10 digits for HCP API)
    let phone = '0000000000'; // Default fallback (10 zeros to meet HCP 10-20 char requirement)
    if (rawPhone) {
      const cleanedPhone = rawPhone.replace(/\D/g, ''); // Remove non-digits
      if (cleanedPhone.length >= 10 && cleanedPhone.length <= 15) {
        phone = cleanedPhone; // Valid phone number
      }
      // If invalid (not 10-15 digits), keep default "0000000000"
    }

    // Build notes from lead information
    const notes = this.buildNotesFromLead(leadData);

    const customerData = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email?.trim() || null,
      mobile_number: phone, // Always has a value (either valid phone or "000")
      notes: notes
    };

    // Remove empty values
    Object.keys(customerData).forEach(key => {
      if (!customerData[key]) {
        delete customerData[key];
      }
    });

    logger.log('[CustomerService] Mapped lead to customer data:', {
      firstName: customerData.first_name,
      lastName: customerData.last_name,
      hasEmail: !!customerData.email,
      phone: customerData.mobile_number,
      phoneIsPlaceholder: customerData.mobile_number === '0000000000',
      hasPhone: !!customerData.mobile_number,
      notesLength: customerData.notes?.length || 0
    });

    return customerData;
  }

  /**
   * Build notes from lead information
   * @param {Object} leadData - Lead data from questionnaire
   * @returns {string} Formatted notes for customer
   */
  buildNotesFromLead(leadData) {
    const notes = [];
    const { formData, stepName, sessionId, createdAt, updatedAt } = leadData;

    // Add lead source indicator
    notes.push('*** CUSTOMER CREATED FROM QUESTIONNAIRE LEAD ***');
    notes.push(`Lead ID: ${leadData.id}`);
    notes.push(`Session: ${sessionId?.substring(0, 8) || 'N/A'}`);
    notes.push(`Step: ${stepName || 'Unknown'}`);

    // Add property information
    if (formData?.address) {
      notes.push(`\nAddress: ${formData.address}`);
    }
    if (formData?.residenceType || formData?.propertyType) {
      notes.push(`Property Type: ${formData.residenceType || formData.propertyType}`);
    }
    if (formData?.squareFootage) {
      notes.push(`Square Footage: ${formData.squareFootage} sq ft`);
    }
    if (formData?.bedrooms || formData?.bathrooms) {
      const bedBath = [
        formData.bedrooms ? `${formData.bedrooms} bed` : '',
        formData.bathrooms ? `${formData.bathrooms} bath` : ''
      ].filter(Boolean).join(', ');
      if (bedBath) notes.push(`Size: ${bedBath}`);
    }

    // Add service information
    if (formData?.cleaningType || formData?.serviceType) {
      notes.push(`\nService: ${formData.cleaningType || formData.serviceType}`);
    }
    if (formData?.frequency) {
      notes.push(`Frequency: ${formData.frequency}`);
    }
    if (formData?.cleanByDate) {
      notes.push(`Clean By: ${formData.cleanByDate}`);
    }
    if (formData?.lastCleaned) {
      notes.push(`Last Cleaned: ${formData.lastCleaned}`);
    }
    if (formData?.cleanlinessLevel) {
      notes.push(`Condition: ${formData.cleanlinessLevel}`);
    }

    // Add additional details
    if (formData?.specialOccasion) {
      notes.push(`\nOccasion: ${formData.specialOccasion}`);
    }
    if (formData?.decisionMaker) {
      notes.push(`Decision Maker: ${formData.decisionMaker}`);
    }
    if (formData?.accessInstructions) {
      notes.push(`Access: ${formData.accessInstructions}`);
    }

    // Add questionnaire metadata
    if (formData?.questionnaire_type) {
      notes.push(`\nType: ${formData.questionnaire_type}`);
    }
    if (formData?.source) {
      notes.push(`Source: ${formData.source}`);
    }

    // Add timestamps
    const leadDate = new Date(updatedAt || createdAt || Date.now());
    notes.push(`\nLead Date: ${leadDate.toLocaleDateString()}`);
    notes.push(`Created: ${new Date().toISOString()}`);

    // Join notes and ensure length limit
    const fullNotes = notes.join('\n');
    
    // Truncate to 254 characters if needed
    if (fullNotes.length > 254) {
      return fullNotes.substring(0, 251) + '...';
    }
    
    return fullNotes;
  }

  /**
   * Validate customer data before sending to API
   * @param {Object} customerData - Customer data to validate
   * @returns {Object} Validation result with isValid and errors
   */
  validateCustomerData(customerData) {
    const errors = [];

    // Required fields
    if (!customerData.first_name || customerData.first_name.trim().length === 0) {
      errors.push('First name is required');
    }
    if (!customerData.last_name || customerData.last_name.trim().length === 0) {
      errors.push('Last name is required');
    }

    // Field length validations
    if (customerData.first_name && customerData.first_name.length > 100) {
      errors.push('First name must be 100 characters or less');
    }
    if (customerData.last_name && customerData.last_name.length > 100) {
      errors.push('Last name must be 100 characters or less');
    }
    if (customerData.company && customerData.company.length > 200) {
      errors.push('Company name must be 200 characters or less');
    }
    if (customerData.notes && customerData.notes.length > 254) {
      errors.push('Notes must be 254 characters or less');
    }

    // Email validation
    if (customerData.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(customerData.email)) {
        errors.push('Email address is not valid');
      }
    }

    // Phone number validations
    const phoneFields = ['mobile_number', 'home_number', 'work_number'];
    phoneFields.forEach(field => {
      if (customerData[field]) {
        const phone = customerData[field].replace(/\D/g, ''); // Remove non-digits

        // Allow "0000000000" as a special placeholder for missing/invalid phone numbers
        if (phone === '0000000000') {
          return; // Skip validation for placeholder
        }

        if (phone.length < 10 || phone.length > 15) {
          errors.push(`${field.replace('_', ' ')} must be between 10-15 digits`);
        }
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

// Export singleton instance
export const customerService = new CustomerService();
export default customerService;